import { type ReactNode, useMemo, useState } from "react";
import { History, RefreshCw, Undo2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useAplicaTargetareSnapshot } from "@/hooks/api/mutate/useAplicaTargetareSnapshot";
import { useRevinoTargetareSnapshot } from "@/hooks/api/mutate/useRevinoTargetareSnapshot";
import { useGetTargetareSnapshotDiferente } from "@/hooks/api/query/useGetTargetareSnapshotDiferente";
import { useGetTargetareSnapshotJob } from "@/hooks/api/query/useGetTargetareSnapshotJob";
import { useGetTargetareSnapshoturi } from "@/hooks/api/query/useGetTargetareSnapshoturi";
import { useToast } from "@/hooks/use-toast";
import type {
  TargetareSnapshotAdmin,
  TargetareSursaSnapshot,
} from "@/types/targetare";
import {
  etichetaStareJob,
  etichetaStareSnapshot,
  formatContorSnapshot,
  poateFiAplicat,
  poateReveniLa,
  snapshotActiv,
  snapshoturiPentruSursa,
  tonStareJob,
  tonStareSnapshot,
} from "@/utils/targetareSnapshotFormatting";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { HeroPagina } from "@/pages/AdministrareTargetare/components/HeroPagina";

const CLASA_TON: Record<string, string> = {
  bun: "border-transparent bg-ds-green-100 text-ds-green-800",
  atentie: "border-transparent bg-amber-100 text-amber-800",
  rau: "border-transparent bg-destructive/15 text-destructive",
  neutru: "border-transparent bg-muted text-muted-foreground",
};

function BadgeStare({ stare }: { stare: TargetareSnapshotAdmin["stare"] }) {
  return (
    <Badge className={CLASA_TON[tonStareSnapshot(stare)]} variant="outline">
      {etichetaStareSnapshot(stare)}
    </Badge>
  );
}

function BadgeStareJob({ stare }: { stare: string }) {
  return (
    <Badge className={CLASA_TON[tonStareJob(stare)]} variant="outline">
      {etichetaStareJob(stare)}
    </Badge>
  );
}

type SursaSnapshotPanelProps = {
  sursa: TargetareSursaSnapshot;
  descriere: ReactNode;
  comandaScript?: string;
};

export function SursaSnapshotPanel({
  sursa,
  descriere,
  comandaScript,
}: SursaSnapshotPanelProps) {
  const { toast } = useToast();
  const snapshoturi = useGetTargetareSnapshoturi();
  const [selectatId, setSelectatId] = useState<number | null>(null);
  const [aplicaDeschis, setAplicaDeschis] = useState<number | null>(null);
  const [forta, setForta] = useState(false);
  const [revinoDeschis, setRevinoDeschis] = useState<number | null>(null);

  const randuri = useMemo(
    () => snapshoturiPentruSursa(snapshoturi.data ?? [], sursa),
    [snapshoturi.data, sursa]
  );
  const activ = useMemo(() => snapshotActiv(randuri), [randuri]);
  const idAfisat = selectatId ?? activ?.id ?? randuri[0]?.id ?? null;

  const diferente = useGetTargetareSnapshotDiferente(idAfisat);
  const job = useGetTargetareSnapshotJob(idAfisat);
  const aplica = useAplicaTargetareSnapshot();
  const revino = useRevinoTargetareSnapshot();

  const confirmaAplicarea = () => {
    if (aplicaDeschis === null) return;
    aplica.mutate(
      { id: aplicaDeschis, forta },
      {
        onSuccess: (r) => {
          const d = r.data;
          if (d.stare === "programat") {
            toast({
              title: "Job programat",
              description: `Jobul ${d.tip} (#${d.job_id}) a fost programat pentru snapshotul #${d.snapshot_id}.`,
            });
          } else if (d.stare === "deja_programat") {
            toast({
              title: "Există deja un job în curs",
              description: `Un job de tip ${d.tip} rulează deja pentru această sursă.`,
            });
          } else {
            toast({
              title: "Aplicarea nu a pornit",
              description: d.mesaj,
              variant: "destructive",
            });
          }
        },
        onError: (e) =>
          toast({
            title: "Aplicarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
    setAplicaDeschis(null);
    setForta(false);
  };

  const confirmaRevenirea = () => {
    if (revinoDeschis === null) return;
    revino.mutate(revinoDeschis, {
      onSuccess: (r) => {
        const d = r.data;
        if (d.stare === "activ") {
          toast({
            title: "Snapshotul a redevenit activ",
            description: d.atentie,
          });
        } else if (d.stare === "deja_activ") {
          toast({ title: "Acest snapshot era deja activ" });
        } else {
          toast({
            title: "Revenirea a eșuat",
            description: d.mesaj,
            variant: "destructive",
          });
        }
      },
      onError: (e) =>
        toast({
          title: "Revenirea a eșuat",
          description: e.message,
          variant: "destructive",
        }),
    });
    setRevinoDeschis(null);
  };

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Sursa activă"
        descriere={descriere}
        isLoading={snapshoturi.isLoading}
        coloane={4}
        tiles={
          activ
            ? [
                { eticheta: "Etichetă", valoare: activ.eticheta ?? "–" },
                {
                  eticheta: "Data publicării",
                  valoare: activ.data_publicarii ?? "–",
                },
                {
                  eticheta: "Importat la",
                  valoare: formatDataOra(activ.importat_la),
                },
                { eticheta: "Rânduri", valoare: activ.randuri ?? null },
                {
                  eticheta: "Noi",
                  valoare: activ.noi ?? null,
                  ton: (activ.noi ?? 0) > 0 ? "bun" : "implicit",
                },
                {
                  eticheta: "Modificate",
                  valoare: activ.modificate ?? null,
                },
                {
                  eticheta: "Inactivate",
                  valoare: activ.inactivate ?? null,
                  ton: (activ.inactivate ?? 0) > 0 ? "atentie" : "implicit",
                },
              ]
            : []
        }
      />

      {!snapshoturi.isLoading && !activ && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Nu există niciun snapshot activ pentru această sursă încă. Rulați
          scriptul local și apoi apăsați „Aplică” pe snapshotul creat.
        </p>
      )}

      {snapshoturi.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Snapshot-urile nu au putut fi încărcate: {snapshoturi.error.message}
        </p>
      )}

      <div className="rounded-md border">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium">Istoricul snapshot-urilor</p>
        </div>
        {snapshoturi.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : randuri.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            Niciun snapshot importat încă pentru această sursă.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Etichetă</th>
                  <th className="px-3 py-1.5 font-medium">Stare</th>
                  <th className="px-3 py-1.5 font-medium">Importat la</th>
                  <th className="px-3 py-1.5 font-medium">Rânduri</th>
                  <th className="px-3 py-1.5 font-medium">
                    Noi / Modif. / Inactiv.
                  </th>
                  <th className="px-3 py-1.5 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {randuri.map((r) => (
                  <tr
                    key={r.id}
                    className={`cursor-pointer border-t align-top hover:bg-muted/40 ${
                      idAfisat === r.id ? "bg-ds-blue-50/50" : ""
                    }`}
                    onClick={() => setSelectatId(r.id)}
                  >
                    <td className="px-3 py-1.5">{r.eticheta ?? `#${r.id}`}</td>
                    <td className="px-3 py-1.5">
                      <BadgeStare stare={r.stare} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">
                      {formatDataOra(r.importat_la)}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {formatContorSnapshot(r.randuri)}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">
                      {formatContorSnapshot(r.noi)} /{" "}
                      {formatContorSnapshot(r.modificate)} /{" "}
                      {formatContorSnapshot(r.inactivate)}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-2">
                        {poateFiAplicat(r) && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectatId(r.id);
                              setAplicaDeschis(r.id);
                            }}
                          >
                            Aplică
                          </Button>
                        )}
                        {poateReveniLa(r) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRevinoDeschis(r.id);
                            }}
                          >
                            <Undo2 className="mr-1 h-3.5 w-3.5" />
                            Revino
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {idAfisat !== null && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-md border">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <p className="text-sm font-medium">
                Diferențe — snapshot #{idAfisat}
              </p>
              <Button
                size="sm"
                variant="ghost"
                disabled={diferente.isFetching}
                onClick={() => diferente.refetch()}
                title="Verifică din nou"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${diferente.isFetching ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
            {diferente.isLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : !diferente.data ? (
              <p className="p-3 text-sm text-muted-foreground">
                Fără date de diferență.
              </p>
            ) : diferente.data.faza === "staging" ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 p-3 text-sm">
                <dt className="text-muted-foreground">Rânduri în staging</dt>
                <dd className="tabular-nums">
                  {formatNumar(diferente.data.randuri)}
                </dd>
                <dt className="text-muted-foreground">Firme curente</dt>
                <dd className="tabular-nums">
                  {formatNumar(diferente.data.firme_curente)}
                </dd>
                <dt className="text-muted-foreground">Noi</dt>
                <dd className="tabular-nums">
                  {formatNumar(diferente.data.noi)}
                </dd>
                <dt className="text-muted-foreground">Dispărute</dt>
                <dd className="tabular-nums">
                  {formatNumar(diferente.data.disparute)}
                </dd>
                <dt className="text-muted-foreground">Fără CUI</dt>
                <dd className="tabular-nums">
                  {formatNumar(diferente.data.fara_cui)}
                </dd>
                {diferente.data.coduri_stare_fara_categorie.length > 0 && (
                  <>
                    <dt className="col-span-2 pt-2 font-medium text-destructive">
                      Coduri de stare fără categorie (blochează aplicarea)
                    </dt>
                    <dd className="col-span-2 break-words text-destructive">
                      {diferente.data.coduri_stare_fara_categorie.join(", ")}
                    </dd>
                  </>
                )}
              </dl>
            ) : (
              <div className="space-y-3 p-3 text-sm">
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">Rânduri</dt>
                  <dd className="tabular-nums">
                    {formatContorSnapshot(diferente.data.randuri)}
                  </dd>
                  <dt className="text-muted-foreground">Noi</dt>
                  <dd className="tabular-nums">
                    {formatContorSnapshot(diferente.data.noi)}
                  </dd>
                  <dt className="text-muted-foreground">Modificate</dt>
                  <dd className="tabular-nums">
                    {formatContorSnapshot(diferente.data.modificate)}
                  </dd>
                  <dt className="text-muted-foreground">Inactivate</dt>
                  <dd className="tabular-nums">
                    {formatContorSnapshot(diferente.data.inactivate)}
                  </dd>
                </dl>
                {diferente.data.istoric.length > 0 && (
                  <div>
                    <p className="font-medium">Modificări pe coloană</p>
                    <ul className="mt-1 space-y-0.5 text-muted-foreground">
                      {diferente.data.istoric.map((i) => (
                        <li key={i.coloana}>
                          {i.coloana}: {formatNumar(i.randuri)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-md border">
            <p className="border-b px-3 py-2 text-sm font-medium">
              Jobul de aplicare
            </p>
            {job.isLoading ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-4 w-full" />
              </div>
            ) : (job.data ?? []).length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">
                Niciun job rulat pentru acest snapshot.
              </p>
            ) : (
              <ul className="divide-y text-sm">
                {(job.data ?? []).map((j) => (
                  <li key={j.id} className="space-y-1 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs">
                        #{j.id} · {j.tip}
                      </span>
                      <BadgeStareJob stare={j.stare} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDataOra(j.inceput_la)}
                      {j.sfarsit_la ? ` → ${formatDataOra(j.sfarsit_la)}` : ""}
                    </p>
                    {j.jurnal && (
                      <p className="whitespace-pre-wrap break-words text-xs">
                        {j.jurnal}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {comandaScript && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <p className="font-medium">Actualizare din scriptul local</p>
          <p className="mt-1 text-muted-foreground">
            Rulați comanda de mai jos (procedura completă în{" "}
            <code className="font-mono">scripts/actualizare/README.md</code>).
            Scriptul creează snapshotul în starea „în curs”; aplicarea se face
            din acest ecran, cu „Aplică”.
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-background p-2 font-mono text-xs">
            {comandaScript}
          </pre>
        </div>
      )}

      <AlertDialog
        open={aplicaDeschis !== null}
        onOpenChange={(o) => !o && setAplicaDeschis(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Aplici snapshotul #{aplicaDeschis}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se programează un job care calculează diferențele față de starea
              curentă și le aplică (istoric, inactivări, recalculul
              statisticilor). Operația rulează în fundal; starea apare mai jos
              și în pagina Activitate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={forta} onCheckedChange={setForta} />
            Forțează aplicarea chiar dacă pragurile de siguranță (±5 %,
            inactivate &gt; 1 %) nu trec
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setForta(false)}>
              Renunță
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmaAplicarea}>
              Aplică
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revinoDeschis !== null}
        onOpenChange={(o) => !o && setRevinoDeschis(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Revii la snapshotul #{revinoDeschis}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Acest snapshot devine din nou activ (eticheta și contoarele
              afișate); datele rămân cele aplicate ultima dată — revenirea nu
              re-aplică diferențele.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Renunță</AlertDialogCancel>
            <AlertDialogAction onClick={confirmaRevenirea}>
              Revino
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
